import React, { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  MapPin,
  Building,
  Globe,
  Building2,
  Edit,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { Site, Organization } from "../types";

interface SiteTreePanelProps {
  organizations: Organization[];
  selectedSite: Site | null;
  onSiteSelect: (site: Site | null) => void;
  // 新增的可选参数，用于站点管理功能
  onEditSite?: (site: Site) => void;
  onDeleteSite?: (site: Site) => void;
  // 是否启用编辑模式
  enableEditMode?: boolean;
  // Add alert rule management
  onManageAlerts?: (site: Site) => void;
}

const SiteTreePanel: React.FC<SiteTreePanelProps> = ({
  organizations,
  selectedSite,
  onSiteSelect,
  onEditSite,
  onDeleteSite,
  enableEditMode = false,
  onManageAlerts,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <div
      className={`bg-white border-r border-gray-200 flex flex-col h-full transition-all duration-200 ${
        isCollapsed ? "w-16" : "w-72"
      }`}
    >
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        {!isCollapsed && (
          <h2 className="text-lg font-semibold text-gray-800">Sites</h2>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-2 rounded hover:bg-gray-100"
        >
          <ChevronLeft
            className={`w-5 h-5 transition-transform ${
              isCollapsed ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {!isCollapsed && (
          <>
            {organizations.map((organization) => (
              <OrganizationNode
                key={organization.id}
                organization={organization}
                level={0}
                selectedSite={selectedSite}
                onSiteSelect={onSiteSelect}
                onEditSite={onEditSite}
                onDeleteSite={onDeleteSite}
                enableEditMode={enableEditMode}
                onManageAlerts={onManageAlerts}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

interface OrganizationNodeProps {
  organization: Organization;
  level: number;
  selectedSite: Site | null;
  onSiteSelect: (site: Site | null) => void;
  onEditSite?: (site: Site) => void;
  onDeleteSite?: (site: Site) => void;
  enableEditMode?: boolean;
  onManageAlerts?: (site: Site) => void;
}

const OrganizationNode: React.FC<OrganizationNodeProps> = ({
  organization,
  level,
  selectedSite,
  onSiteSelect,
  onEditSite,
  onDeleteSite,
  enableEditMode,
  onManageAlerts,
}) => {
  const [expanded, setExpanded] = useState(level < 1);
  const hasChildren = organization.sites.length > 0;

  const getIcon = () => {
    return <Building2 className="w-4 h-4 text-purple-600" />;
  };

  return (
    <div>
      <div
        className={`flex items-center space-x-2 py-2 text-sm cursor-pointer rounded-md ${"text-gray-700 hover:bg-gray-50"}`}
        style={{ paddingLeft: level >= 0 ? `${level * 16 + 12}px` : `12px` }}
      >
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="p-1 hover:bg-gray-200 rounded"
          >
            {expanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
          </button>
        ) : (
          <div className="w-5" /> // Placeholder for alignment
        )}
        {getIcon()}
        <span className="font-medium">{organization.name}</span>
        <span className="text-xs text-gray-500">
          ({organization.deviceCount})
        </span>
      </div>
      {expanded && hasChildren && (
        <div className="pl-4">
          {organization.sites.map((site) => (
            <SiteNode
              key={site.id}
              site={site}
              level={level + 1}
              selectedSite={selectedSite}
              onSiteSelect={onSiteSelect}
              onEditSite={onEditSite}
              onDeleteSite={onDeleteSite}
              enableEditMode={enableEditMode}
              onManageAlerts={onManageAlerts}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface SiteNodeProps {
  site: Site;
  level: number;
  selectedSite: Site | null;
  onSiteSelect: (site: Site | null) => void;
  onEditSite?: (site: Site) => void;
  onDeleteSite?: (site: Site) => void;
  enableEditMode?: boolean;
  onManageAlerts?: (site: Site) => void;
}

const SiteNode: React.FC<SiteNodeProps> = ({
  site,
  level,
  selectedSite,
  onSiteSelect,
  onEditSite,
  onDeleteSite,
  enableEditMode = false,
  onManageAlerts,
}) => {
  const [expanded, setExpanded] = useState(level < 2);
  const [showActions, setShowActions] = useState(false);
  const hasChildren = site.children && site.children.length > 0;

  const isSelected = selectedSite?.id === site.id;

  // 计算设备数量：包括当前站点的设备数量和子站点的设备数量
  const getDeviceCount = (site: Site): number => {
    // 计算当前站点的设备数量
    let count = 0;

    // 添加 edge_devices 数量
    if (site.edge_devices) {
      count += site.edge_devices.length;
    }

    // 添加 iqwatch_devices 数量
    if (site.iqwatch_devices) {
      count += site.iqwatch_devices.length;
    }

    // 如果没有设备数组，回退到使用 deviceCount
    if (!site.edge_devices && !site.iqwatch_devices) {
      count = site.deviceCount || 0;
    }

    // 递归计算子站点的设备数量
    if (site.children) {
      count += site.children.reduce(
        (sum, child) => sum + getDeviceCount(child),
        0
      );
    }

    return count;
  };

  const deviceCount = getDeviceCount(site);

  const getIcon = () => {
    switch (site.type) {
      case "region":
        return <Globe className="w-4 h-4 text-blue-600" />;
      case "building":
        return <Building className="w-4 h-4 text-green-600" />;
      case "location":
        return <MapPin className="w-4 h-4 text-orange-600" />;
      default:
        return null;
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEditSite?.(site);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDeleteSite?.(site);
  };

  return (
    <div>
      <div
        onClick={() => onSiteSelect(site)}
        onMouseEnter={() => enableEditMode && setShowActions(true)}
        onMouseLeave={() => enableEditMode && setShowActions(false)}
        className={`flex items-center space-x-2 py-2 text-sm cursor-pointer rounded-md group ${
          isSelected
            ? "bg-blue-100 text-blue-700"
            : "text-gray-700 hover:bg-gray-50"
        }`}
        style={{ paddingLeft: `${level * 16 + 12}px` }}
      >
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="p-1 hover:bg-gray-200 rounded"
          >
            {expanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
          </button>
        ) : (
          <div className="w-5" /> // Placeholder for alignment
        )}
        {getIcon()}
        <span className="font-medium flex-1">{site.name}</span>
        <span className="text-xs text-gray-500">({deviceCount})</span>

        {/* Action buttons - 只在编辑模式下显示 */}
        {enableEditMode &&
          showActions &&
          (onEditSite || onDeleteSite || onManageAlerts) && (
            <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {onManageAlerts && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onManageAlerts(site);
                  }}
                  className="p-1 hover:bg-gray-200 rounded text-gray-500 hover:text-orange-600"
                  title="Manage alerts"
                >
                  <AlertTriangle className="w-3 h-3" />
                </button>
              )}
              {onEditSite && (
                <button
                  onClick={handleEdit}
                  className="p-1 hover:bg-gray-200 rounded text-gray-500 hover:text-blue-600"
                  title="Edit site"
                >
                  <Edit className="w-3 h-3" />
                </button>
              )}
              {onDeleteSite && (
                <button
                  onClick={handleDelete}
                  className="p-1 hover:bg-gray-200 rounded text-gray-500 hover:text-red-600"
                  title="Delete site"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
      </div>
      {expanded && hasChildren && (
        <div className="pl-4">
          {site.children?.map((child) => (
            <SiteNode
              key={child.id}
              site={child}
              level={level + 1}
              selectedSite={selectedSite}
              onSiteSelect={onSiteSelect}
              onEditSite={onEditSite}
              onDeleteSite={onDeleteSite}
              enableEditMode={enableEditMode}
              onManageAlerts={onManageAlerts}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SiteTreePanel;
